/**
 * useEntityLinkedEpisodes — lists the most recent episodes (max 50) that
 * touched a given entity, via `episodes.list_for_entity`.
 *
 * Plan item #5 (docs/plans/context-panel-from-graph.md).
 */

import { useQuery } from "@tanstack/react-query";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";

export interface EpisodeLinkSummary {
  readonly episode_id: string;
  readonly title: string;
  readonly status: string;
  readonly is_archived: boolean;
  readonly link_kinds: readonly string[];
  readonly updated_at: string;
  readonly isEmpty: boolean;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useEntityLinkedEpisodes(entityId: string | undefined) {
  const runtime = useAppRuntime();
  const valid = entityId && UUID_RE.test(entityId);
  return useQuery({
    queryKey: ["episodes", "list_for_entity", entityId ?? null],
    queryFn: () =>
      runtime.transport.rpc<readonly EpisodeLinkSummary[]>(
        "episodes.list_for_entity",
        { entity_id: entityId, limit: 50, offset: 0 },
      ),
    enabled: Boolean(valid),
    staleTime: 10_000,
  });
}
